/**
 * @voltxampere/ui — WireTool (Sprint 13)
 *
 * Two-click wire placement with a live Manhattan ghost preview.
 *
 *   idle → pointerdown on terminal → routing (start = terminal pos)
 *   routing → pointermove → ghost ManhattanPath updates
 *   routing → Tab → posture toggle
 *   routing → pointerdown on terminal → WireCommand, idle
 *   routing → pointerdown on grid → WireCommand at snapped point, idle
 *   routing → Esc → idle (cancel ghost)
 *
 * Sprint 13 implementation note: a wire connects two terminals. If
 * the user drops onto an empty grid intersection, we still create a
 * wire whose endpoint is just a hanging point — Sprint 14 net-merge
 * will surface that as a "dangling endpoint" warning. The current
 * behaviour matches KiCad's permissive intermediate-state UX.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { BatchCommand, MergeNetsCommand, WireCommand } from "../commands/index.js";
import { inferPosture, routeManhattan, togglePosture, } from "../manhattan.js";
export const WIRE_GRID_SIZE = 16;
/**
 * Wire snap radius in world units (Sprint 25 R2 lane 4D-A).
 *
 * Pre-Lane-4D-A: 8 px Manhattan box (`Math.abs(dx) <= 8 &&
 * Math.abs(dy) <= 8`). v1 prototype: 25 px Euclidean
 * (`~/Desktop/VoltXAmpere/src/interaction/pin-snap.js` SNAP_RADIUS).
 * Lane 4D-A compromise: 20 px Euclidean — looser than the old
 * Manhattan box (~3× the diagonal reach), tighter than v1 (avoids
 * snapping to pins way off-screen). `findTerminalAt` now uses
 * `Math.hypot` so the snap zone is a circle rather than a square.
 */
export const WIRE_TERMINAL_TOLERANCE = 20;
export class WireTool {
    id = "wire";
    cursor = "crosshair";
    state = { type: "idle" };
    ghostNodeId = "vxa-wire-ghost";
    onPointerDown(e, ctx) {
        if (e.button !== 0)
            return;
        const circuit = ctx.circuit.value;
        if (!circuit)
            return;
        const world = ctx.worldFromEvent(e);
        const snapped = ctx.viewport.snapToGrid(world, WIRE_GRID_SIZE);
        const terminal = findTerminalAt(circuit.components, world, WIRE_TERMINAL_TOLERANCE);
        if (this.state.type === "idle") {
            const start = terminal?.position ?? snapped;
            this.state = {
                type: "routing",
                start,
                startNetId: terminal?.netId ?? null,
                posture: "horizontal-first",
                path: routeManhattan(start, start, "horizontal-first"),
                cursorWorld: start,
            };
            ctx.invalidate();
            return;
        }
        // Second click — complete the wire.
        const end = terminal?.position ?? snapped;
        const path = routeManhattan(this.state.start, end, this.state.posture);
        if (path.segments.length === 0) {
            // Same point click — cancel.
            this.cancel(ctx);
            return;
        }
        // Sprint 14 (ADR-0021) — D3 closure. When both endpoints
        // already belong to distinct nets we resolve the ambiguity by
        // merging them with `MergeNetsCommand` (ground wins; otherwise
        // alphabetic tiebreak) and then placing the wire on the
        // surviving net id. The two commands are wrapped in a
        // `BatchCommand` so a single Cmd+Z reverts both. Sprint 13's
        // `console.warn` fallback is gone.
        const startNet = this.state.startNetId;
        const endNet = terminal?.netId ?? null;
        let mergeCommand = null;
        let wireNetId;
        if (startNet && endNet && startNet !== endNet) {
            mergeCommand = new MergeNetsCommand(startNet, endNet, circuit);
            wireNetId = mergeCommand.winnerNetId;
        }
        else {
            wireNetId = startNet ?? endNet ?? `net-${randomId()}`;
        }
        const wires = path.segments.map((seg) => ({
            id: randomId(),
            netId: wireNetId,
            style: "direct",
            path: [seg.start, seg.end],
        }));
        const wireCommand = new WireCommand(wires);
        const command = mergeCommand
            ? new BatchCommand([mergeCommand, wireCommand], "Wire connected, nets merged")
            : wireCommand;
        ctx.circuit.value = ctx.undoStack.execute(command, circuit);
        this.state = { type: "idle" };
        ctx.invalidate();
    }
    onPointerMove(e, ctx) {
        if (this.state.type !== "routing")
            return;
        const world = ctx.worldFromEvent(e);
        const snapped = ctx.viewport.snapToGrid(world, WIRE_GRID_SIZE);
        // First non-zero displacement seeds the posture.
        if (this.state.cursorWorld.x === this.state.start.x &&
            this.state.cursorWorld.y === this.state.start.y &&
            (snapped.x !== this.state.start.x || snapped.y !== this.state.start.y)) {
            this.state = {
                ...this.state,
                posture: inferPosture(this.state.start, snapped),
            };
        }
        this.state = {
            ...this.state,
            cursorWorld: snapped,
            path: routeManhattan(this.state.start, snapped, this.state.posture),
        };
        ctx.invalidate();
    }
    onKeyDown(e, ctx) {
        if (this.state.type !== "routing")
            return;
        if (e.key === "Tab") {
            e.preventDefault();
            this.state = {
                ...this.state,
                posture: togglePosture(this.state.posture),
                path: routeManhattan(this.state.start, this.state.cursorWorld, togglePosture(this.state.posture)),
            };
            ctx.invalidate();
        }
        if (e.key === "Escape") {
            this.cancel(ctx);
        }
    }
    onLeave(ctx) {
        if (this.state.type === "routing")
            this.cancel(ctx);
    }
    /** Test surface — current ghost path (or null when idle). */
    get ghostPath() {
        return this.state.type === "routing" ? this.state.path : null;
    }
    /** Test surface — current posture (or null when idle). */
    get posture() {
        return this.state.type === "routing" ? this.state.posture : null;
    }
    /** Helper for the renderer — emit ghost-wire SceneNodes. */
    ghostNodes() {
        if (this.state.type !== "routing")
            return [];
        return [
            {
                kind: "ghost-wire",
                id: this.ghostNodeId,
                segments: this.state.path.segments,
            },
        ];
    }
    cancel(ctx) {
        this.state = { type: "idle" };
        ctx.invalidate();
    }
}
/**
 * Find the closest terminal to the cursor within `tol` world units.
 *
 * Sprint 25 R2 lane 4D-A: switched from Manhattan box
 * (`Math.abs(dx) <= tol && Math.abs(dy) <= tol`) to Euclidean
 * radius (`Math.hypot(dx, dy) <= tol`). Mirrors v1's `pinSnap`
 * geometry. Linear scan picks the nearest match so two pins close
 * together do not arbitrarily fight for the cursor. Exported so
 * the snap-radius vitest suite can exercise the geometry directly
 * without spinning up a full `ToolContext` stub.
 */
export function findTerminalAt(components, world, tol) {
    let best = null;
    let bestDist = tol;
    for (const comp of components) {
        for (const term of comp.terminals) {
            const pos = term.position;
            const dist = Math.hypot(pos.x - world.x, pos.y - world.y);
            if (dist < bestDist) {
                bestDist = dist;
                best = { position: pos, netId: term.netId };
            }
        }
    }
    return best;
}
function randomId() {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    return `id-${Math.random().toString(36).slice(2)}`;
}
//# sourceMappingURL=wire.js.map